import { ConfirmProjectModal, IceDetailsDialog, showToast } from '@ideoz/ui';
import { FolderMinus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CreateScopeDialog, type CreateScopeDialogSubmit } from './components/CreateScopeDialog';
import { ScopesListSection } from './components/ScopesListSection';
import { UploadScopeFilesDialog } from './scopeSuggestions/UploadScopeFilesDialog';
import { useWorkScopes } from './useWorkScopes';

interface WorkScopesModuleProps {
  startupId: string;
  onNavigateToScope: (scopeId: string) => void;
  /** Whether the "New Scope" create dialog is open (driven by the header button). */
  createOpen: boolean;
  onCreateClose: () => void;
  /** Whether the "Suggest scopes" upload dialog is open (driven by the header). */
  suggestOpen: boolean;
  onSuggestClose: () => void;
  onOpenCreate: () => void;
  onOpenSuggest: () => void;
}

/**
 * Work Scopes feature module: the scope list plus every scope dialog (create,
 * edit, delete, ICE details, suggest-from-files). All of them run on this
 * module's single facade + ICE instance, so creating or suggesting a scope flips
 * the list badge to "Scoring…" directly — no cross-component bridge needed. The
 * header buttons that open the create/suggest dialogs live on the page and drive
 * the `createOpen` / `suggestOpen` props.
 */
export function WorkScopesModule({
  startupId,
  onNavigateToScope,
  createOpen,
  onCreateClose,
  suggestOpen,
  onSuggestClose,
  onOpenCreate,
  onOpenSuggest,
}: WorkScopesModuleProps): JSX.Element {
  const {
    facade,
    ice,
    list,
    scopesWithIce,
    iceDialog,
    openIceDetails,
    closeIceDetails,
    scopeToDelete,
    requestDeleteScope,
    cancelDeleteScope,
    confirmDeleteScope,
  } = useWorkScopes({ startupId, navigateToScope: onNavigateToScope });
  const { t } = useTranslation();

  // Create or edit, with success/error toasts. Returns the new scope id on
  // create so the dialog can gate on file indexing.
  const handleSubmit = async (data: CreateScopeDialogSubmit): Promise<string | void> => {
    const editing = facade.editingScope;
    try {
      if (editing) {
        await facade.updateScope(editing.id, data);
        showToast({ message: t('workScopes.toast.updated'), severity: 'success' });
        list.refetch();
        return;
      }
      const scopeId = await facade.createScope(data);
      showToast({ message: t('workScopes.toast.created'), severity: 'success' });
      list.refetch();
      return scopeId;
    } catch (err) {
      showToast({
        message: editing
          ? t('workScopes.toast.updateFailed')
          : t('workScopes.toast.createFailed'),
        severity: 'error',
      });
      throw err;
    }
  };

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-10">
      <ScopesListSection
        title={t('workScopes.title')}
        subtitle={t('workScopes.subtitle')}
        scopes={scopesWithIce}
        isLoading={list.isLoading}
        onCardClick={facade.navigateToScope}
        onCardEdit={(scope) => facade.openEditDialog({ ...scope, name: scope.title })}
        onCardDelete={requestDeleteScope}
        onCardEvaluate={(scopeId) => void ice.evaluate(scopeId)}
        onCardOpenIce={openIceDetails}
        scoringScopeIds={ice.scoringScopeIds}
        onNewScope={onOpenCreate}
        onSuggestScopes={onOpenSuggest}
        search={list.search}
        onSearchChange={list.setSearch}
        onSearchSubmit={list.submitSearch}
        onClearSearch={list.clearSearch}
        isSearchActive={list.isSearchActive}
        sort={list.sort}
        onSortChange={list.setSort}
        pagination={list.pagination}
        onPageChange={list.setPage}
      />

      <ConfirmProjectModal
        isOpen={!!scopeToDelete}
        onClose={cancelDeleteScope}
        onCreate={confirmDeleteScope}
        title={t('workScopes.delete.title')}
        info={{
          title: t('workScopes.delete.infoTitle'),
          desc: t('workScopes.delete.infoDesc'),
        }}
        buttonText={t('workScopes.delete.button')}
        buttonType="danger"
        iconName={FolderMinus}
        inputLabel={t('workScopes.delete.inputLabel')}
        expectedProjectName={scopeToDelete?.title}
        targetAction="delete"
        isLoading={facade.isMutatingScope}
      />

      {/* One dialog for both create (header button → `createOpen`) and edit
          (card → `facade.editingScope`), on the module's own facade + ICE. */}
      <CreateScopeDialog
        open={createOpen || facade.createDialogOpen}
        onClose={() => {
          onCreateClose();
          facade.closeDialog();
        }}
        startupId={startupId}
        onEvaluate={ice.evaluate}
        onSubmit={handleSubmit}
        isLoading={facade.isCreatingScope || facade.isMutatingScope}
        initialValues={
          facade.editingScope
            ? {
                title: facade.editingScope.title,
                description: facade.editingScope.description ?? '',
              }
            : undefined
        }
        mode={facade.editingScope ? 'edit' : 'create'}
        scopeId={facade.editingScope?.id}
      />

      <UploadScopeFilesDialog
        isOpen={suggestOpen}
        onClose={onSuggestClose}
        startupId={startupId}
        onScopeAdded={() => {
          list.refetch();
          void ice.refresh();
        }}
        onScopeEvaluate={ice.evaluate}
      />

      <IceDetailsDialog
        open={!!iceDialog.scopeId}
        onClose={closeIceDetails}
        scopeTitle={iceDialog.scopeTitle}
        data={iceDialog.data}
        isLoading={iceDialog.isLoading}
        isScoring={iceDialog.isScoring}
        error={iceDialog.error}
        className="max-w-2xl"
      />
    </section>
  );
}
